import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import BaseCard from './BaseCard';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { UserProfileDropdown } from './UserProfileDropdown';

interface TopNavProps {
  onOpenSidebar?: () => void;
}

export const TopNav: React.FC<TopNavProps> = ({ onOpenSidebar }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Navigation Bar */}
      <BaseCard overflow='visible' className="mt-2 py-5.5 px-7 -my-0">
        <header className="flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={onOpenSidebar}
            className="md:hidden p-1 text-gray-500 hover:text-gray-900 cursor-pointer"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2 md:gap-4 md:flex-none ml-auto md:ml-0">
            <div 
              onClick={() => navigate('/')}
              className="cursor-pointer"
            >
              <img
                src="/holito-logo-black.svg"
                alt="holito"
                className="h-6 md:h-8"
              />
            </div>
          </div>

          {/* Right Side: Settings, User Info */}
          <div className="hidden md:flex items-center gap-6">
            {/* Notification Bell 
            <button className="relative text-gray-600 hover:text-gray-900">
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
            </button> */}

            {/* Settings with Dropdown */}
            <div className="relative" ref={settingsRef}>
              <button
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className="text-gray-600 hover:text-gray-900 cursor-pointer"
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>

              {showSettingsMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-custom py-1 z-20">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setShowSettingsMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowSettingsMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    Settings
                  </button>
                  <a
                    href="https://holito.app/terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    onClick={() => setShowSettingsMenu(false)}
                  >
                    Terms & Conditions
                  </a>
                  <button
                    onClick={async () => {
                      await signOut();
                      setShowSettingsMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
            
            {/* Workspace Dropdown */}
            <UserProfileDropdown />
          </div>
        </header>
      </BaseCard>
    </>
  );
};